import React from "react";
import { Helmet } from "react-helmet-async";
import { MapPin, Star, Clock, Users, Camera, Phone, Car, Sparkles } from "lucide-react";

const KanchMandir = () => {
  const templeImage = "/Indore/temples/kanch0.jpg";
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Kanch Mandir (Glass Temple)",
    "description": "Magnificent Jain temple in Indore entirely made of glass and mirrors, showcasing extraordinary craftsmanship and spiritual beauty",
    "image": templeImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Itwaria Bazaar",
      "addressLocality": "Indore",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Mo-Su 05:00-22:00",
    "priceRange": "Free",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.7",
      "reviewCount": "2100"
    }
  };

  const highlights = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Glass Architecture",
      description: "Entire temple constructed with glass and mirrors creating mesmerizing reflections and divine ambiance"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Intricate Artwork",
      description: "Detailed glass mosaic work depicting Jain Tirthankaras and religious motifs with exceptional craftsmanship"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Spiritual Experience",
      description: "Peaceful meditation spaces and prayer halls offering divine tranquility and spiritual reflection"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Unique Architecture",
      description: "One-of-a-kind temple design that combines traditional Jain architecture with innovative glass work"
    }
  ];

  const visitInfo = [
    { label: "Entry Fee", value: "Free Entry" },
    { label: "Timings", value: "5:00 AM - 10:00 PM (Daily)" },
    { label: "Photography", value: "Allowed with permission" },
    { label: "Best Time", value: "Early morning or evening" },
    { label: "Duration", value: "1-2 hours" },
    { label: "Facilities", value: "Shoe stand, Pure water" }
  ];

  const templeFeatures = [
    {
      title: "Main Prayer Hall",
      description: "Central worship area with glass-encased Jain Tirthankar idols and mirror ceiling",
      highlight: "Divine reflection experience"
    },
    {
      title: "Glass Corridors", 
      description: "Walkways lined with intricate glass artwork and mirror mosaics",
      highlight: "Infinite reflection patterns"
    },
    {
      title: "Meditation Chambers",
      description: "Quiet spaces designed for personal prayer and spiritual contemplation",
      highlight: "Peaceful glass interiors"
    },
    {
      title: "Artistic Panels",
      description: "Detailed glass panels depicting scenes from Jain scriptures and philosophy",
      highlight: "Religious storytelling through glass"
    },
    {
      title: "Mirror Ceiling",
      description: "Magnificent ceiling work with thousands of small mirrors creating magical lighting",
      highlight: "Celestial atmosphere"
    },
    {
      title: "Glass Pillars",
      description: "Structural pillars entirely covered in decorative glass work and mirrors",
      highlight: "Architectural marvel"
    }
  ];

  const nearbyAttractions = [
    { name: "Rajwada Palace", distance: "1 km", time: "5 mins" },
    { name: "Sarafa Bazaar", distance: "0.5 km", time: "3 mins" },
    { name: "Central Museum", distance: "2 km", time: "8 mins" },
    { name: "Lal Bagh Palace", distance: "3 km", time: "10 mins" }
  ];

  const photographyTips = [
    "Early morning light creates beautiful glass reflections",
    "Use natural lighting to capture glass artwork details",
    "Respect photography guidelines and ask permission",
    "Avoid flash photography during prayer times",
    "Capture wide shots to show the temple's full glory",
    "Focus on intricate mirror and glass patterns"
  ];

  return (
    <>
      <Helmet>
        <title>Kanch Mandir Indore - Glass Temple | Jain Architecture | Aaoghumen</title>
        <meta name="description" content="Visit Kanch Mandir (Glass Temple) Indore with Aaoghumen - explore magnificent Jain temple made entirely of glass and mirrors. Complete guide with timings, photography tips, and spiritual significance." />
        <meta name="keywords" content="kanch mandir indore, glass temple indore, jain temple indore, kanch mandir timings, indore glass temple, aaoghumen kanch mandir, jain architecture indore, glass temple photography, indore religious places" />
        
        <meta property="og:title" content="Kanch Mandir - Magnificent Glass Temple | Indore | Aaoghumen" />
        <meta property="og:description" content="Experience the divine beauty of Kanch Mandir, Indore's famous glass temple showcasing extraordinary Jain craftsmanship and spiritual architecture." />
        <meta property="og:image" content={templeImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/indore/kanch-mandir" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Kanch Mandir - Glass Temple Indore | Aaoghumen" />
        <meta name="twitter:description" content="Discover the mesmerizing beauty of glass and mirror architecture at Kanch Mandir, a unique Jain temple in Indore." />
        <meta name="twitter:image" content={templeImage} />
        
        <link rel="canonical" href="https://aaoghumen.com/destinations/indore/kanch-mandir" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-blue-600 via-purple-500 to-pink-600 overflow-hidden">
          <img
            src={templeImage}
            alt="Kanch Mandir Glass Temple Indore Jain architecture"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-yellow-300">Kanch Mandir</span> - Glass Temple
              </h1>
              <p className="text-xl mb-6">
                Marvel at this extraordinary Jain temple entirely crafted from glass and mirrors, where divine spirituality meets breathtaking artistry to create a celestial experience like no other.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.7 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>5 AM - 10 PM</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>Free Entry</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Kanch Mandir</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Kanch Mandir, literally meaning "Glass Temple," stands as one of Indore's most magnificent architectural marvels and a testament to Jain artistic excellence. This extraordinary temple is entirely constructed using glass and mirrors, creating a divine space where light dances through countless reflections, producing an otherworldly spiritual atmosphere. Built by Sir Seth Hukumchand, a prominent Jain merchant, the temple represents the pinnacle of craftsmanship and devotion.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Every surface of the temple, from walls to ceilings, is adorned with intricate glass work and mirror mosaics that depict scenes from Jain scriptures, portraits of Tirthankaras, and geometric patterns that symbolize the infinite nature of the soul. The temple serves not just as a place of worship but as a living gallery of religious art where spirituality and aesthetics merge seamlessly. The interplay of natural and artificial light creates a constantly changing kaleidoscope of colors and patterns.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, experience the divine tranquility and artistic brilliance of Kanch Mandir. Our knowledgeable guides explain the religious significance of the artwork, the history of Jain traditions, and the incredible craftsmanship that went into creating this unique temple. Discover how this sacred space continues to inspire devotees and art lovers from around the world.
              </p>
            </div>
          </section>

          {/* Temple Highlights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Temple Highlights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-100 p-3 rounded-lg mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{highlight.title}</h3>
                  </div>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Temple Features */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Temple Features & Architecture</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templeFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <h3 className="text-xl font-bold text-blue-600 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">{feature.description}</p>
                  <div className="bg-yellow-100 p-2 rounded-lg">
                    <p className="text-yellow-800 font-medium text-xs">{feature.highlight}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Visit Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Information</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitInfo.map((info, index) => (
                  <div key={index} className="border-l-4 border-blue-500 pl-4">
                    <h3 className="font-bold text-gray-800 mb-2">{info.label}</h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Photography & Experience Tips */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Photography & Experience Guide</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-blue-600 mb-4">Photography Tips</h3>
                <p className="text-gray-600 mb-4">Capture the magical interplay of light and reflections in this glass wonderland.</p>
                <ul className="text-sm text-gray-500 space-y-2">
                  {photographyTips.map((tip, index) => (
                    <li key={index} className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-blue-600 mb-4">Spiritual Experience</h3>
                <p className="text-gray-600 mb-4">Immerse yourself in the peaceful atmosphere and divine energy of this sacred space.</p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Early morning visits for serene meditation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Observe evening aarti ceremonies</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Respect temple customs and traditions</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Remove shoes before entering</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-blue-600 mb-4">Art & Architecture</h3>
                <p className="text-gray-600 mb-4">Appreciate the incredible craftsmanship and artistic details throughout the temple.</p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Study intricate glass mosaic patterns</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Observe Tirthankar depictions in glass</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Notice changing light effects throughout day</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Appreciate traditional Jain architectural elements</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Nearby Attractions */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nearby Attractions</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nearbyAttractions.map((attraction, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{attraction.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{attraction.distance}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Car className="w-4 h-4 mr-2 text-green-500" />
                      <span>{attraction.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How to Reach */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">How to Reach</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Public Transport</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Bus:</strong> City buses to Itwaria Bazaar, central location</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Auto:</strong> Available from all areas, ₹30-80 depending on distance</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Shared Tempo:</strong> Frequent services to old city area</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Private Vehicle</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Parking:</strong> Limited street parking available</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Location:</strong> Itwaria Bazaar, near Rajwada Palace</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Navigation:</strong> Search "Kanch Mandir Itwaria" on maps</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contact and Plan Visit */}
          <section className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Experience Divine Glass Architecture</h2>
              <p className="text-xl mb-6">Discover the magical world of glass artistry and spiritual serenity at Kanch Mandir</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 12345-67890</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>Itwaria Bazaar, Indore</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button className="bg-white text-blue-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors">
                  Plan Temple Visit
                </button>
                <button className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-blue-600 transition-colors">
                  Get Directions
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default KanchMandir;